import React, { useMemo, useState, useEffect } from 'react';
import { useSvgStore } from '../../store/svgStore';
import { SvgCanvasElement, SvgNodeElement, SvgSubElement, SvgAnimationProps } from '../../types/svgTypes';

type TabType = 'basic' | 'animation' | 'subElements';

/**
 * 将颜色值规范化为 #xxxxxx 格式
 * 处理 undefined、空字符串、rgb()、rgba()、命名颜色等情况
 * 如果是渐变引用，返回 null
 */
const normalizeColor = (color: string | undefined, fallback: string = '#000000'): string | null => {
  if (!color || color === 'none' || color === 'transparent') {
    return fallback;
  }
  
  // 检查是否为渐变引用
  if (color.trim().startsWith('url(')) {
    return null; // 返回 null 表示是渐变
  }
  
  // 已经是 hex 格式
  if (color.startsWith('#')) {
    // 确保是有效的 hex 颜色
    if (/^#[0-9A-Fa-f]{6}$/.test(color)) {
      return color;
    }
    // 3位 hex 转 6位
    if (/^#[0-9A-Fa-f]{3}$/.test(color)) {
      return `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`;
    }
  }
  
  // 处理 rgb() 和 rgba() 格式
  const rgbMatch = color.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgbMatch) {
    const r = parseInt(rgbMatch[1], 10).toString(16).padStart(2, '0');
    const g = parseInt(rgbMatch[2], 10).toString(16).padStart(2, '0');
    const b = parseInt(rgbMatch[3], 10).toString(16).padStart(2, '0');
    return `#${r}${g}${b}`;
  }
  
  // 常见命名颜色映射
  const namedColors: Record<string, string> = {
    'black': '#000000',
    'white': '#ffffff',
    'red': '#ff0000',
    'green': '#008000',
    'blue': '#0000ff',
    'yellow': '#ffff00',
    'orange': '#ffa500',
    'purple': '#800080',
    'gray': '#808080',
    'grey': '#808080',
    'cyan': '#00ffff',
    'magenta': '#ff00ff',
    'lime': '#00ff00',
    'pink': '#ffc0cb',
    'navy': '#000080',
    'teal': '#008080',
    'maroon': '#800000',
    'olive': '#808000',
    'silver': '#c0c0c0',
    'aqua': '#00ffff',
    'fuchsia': '#ff00ff',
  };
  
  const lowerColor = color.toLowerCase();
  if (namedColors[lowerColor]) {
    return namedColors[lowerColor];
  }
  
  return fallback;
};

/**
 * 获取渐变引用的 ID
 * 例如: "url(#grad)" -> "grad"
 */
const getGradientId = (color: string | undefined): string | null => {
  if (!color) return null;
  const match = color.match(/url\(#([^)]+)\)/);
  return match ? match[1] : null;
};

const PropertiesPanel: React.FC = () => {
  const canvas = useSvgStore((state) => state.canvas);
  const selectedState = useSvgStore((state) => state.selectedState);
  const updateElement = useSvgStore((state) => state.updateElement);
  
  const alignSelectedElements = useSvgStore((state) => state.alignSelectedElements);
  const saveCanvas = useSvgStore((state) => state.saveCanvas);
  const exportSvgFile = useSvgStore((state) => state.exportSvgFile);

  // 子元素编辑相关
  const isSubElementEditMode = useSvgStore((state) => state.isSubElementEditMode);
  const selectedSubElementId = useSvgStore((state) => state.selectedSubElementId);
  const enterSubElementEditMode = useSvgStore((state) => state.enterSubElementEditMode);
  const exitSubElementEditMode = useSvgStore((state) => state.exitSubElementEditMode);
  const selectSubElement = useSvgStore((state) => state.selectSubElement);
  const updateSubElement = useSvgStore((state) => state.updateSubElement);
  const updateNodeAnimation = useSvgStore((state) => state.updateNodeAnimation);
  const parseNodeSubElements = useSvgStore((state) => state.parseNodeSubElements);
  const resetSubElementAttribute = useSvgStore((state) => state.resetSubElementAttribute);

  const [activeTab, setActiveTab] = useState<TabType>('basic');

  const activeElement: SvgCanvasElement | undefined = useMemo(() => {
    if (!canvas || !selectedState.activeElementId) {
      return undefined;
    }
    return canvas.elements.find(
      (element) => element.id === selectedState.activeElementId
    );
  }, [canvas, selectedState.activeElementId]);

  const multiSelection = selectedState.selectedIds.length > 1;
  const node = activeElement as SvgNodeElement;
  const isDisabled = !node;

  // 选中元素时自动解析子元素和动画
  useEffect(() => {
    if (node && node.type === 'svg') {
      if (!node.subElements || node.subElements.length === 0) {
        parseNodeSubElements(node.id);
      }
    }
  }, [node?.id, node?.subElements, parseNodeSubElements]);

  // 查找当前选中的子元素
  const activeSubElement = useMemo(() => {
    if (!node?.subElements || !selectedSubElementId) return null;
    
    const findSubElement = (elements: SvgSubElement[]): SvgSubElement | null => {
      for (const el of elements) {
        if (el.id === selectedSubElementId) return el;
        if (el.children) {
          const found = findSubElement(el.children);
          if (found) return found;
        }
      }
      return null;
    };
    
    return findSubElement(node.subElements);
  }, [node?.subElements, selectedSubElementId]);

  const handleChange = (
    field: keyof SvgNodeElement,
    value: string | number
  ) => {
    if (!activeElement || activeElement.type !== 'svg') {
      return;
    }
    const parsedValue =
      field === 'x' ||
      field === 'y' ||
      field === 'scale' ||
      field === 'rotation' ||
      field === 'opacity'
        ? Number(value)
        : value;
    updateElement(activeElement.id, {
      [field]: parsedValue,
    } as Partial<SvgCanvasElement>);
  };

  const handleAnimationChange = (field: keyof SvgAnimationProps, value: string | number | boolean) => {
    if (!node) return;
    updateNodeAnimation(node.id, { [field]: value });
  };

  const handleSubElementChange = (
    field: keyof SvgSubElement['customAttributes'], 
    value: string | number
  ) => {
    if (!node || !selectedSubElementId) {
      console.warn('handleSubElementChange: 缺少 node 或 selectedSubElementId');
      return;
    }
    console.log('handleSubElementChange:', { nodeId: node.id, subElementId: selectedSubElementId, field, value });
    updateSubElement(node.id, selectedSubElementId, { [field]: value });
  };

  const handleResetAttribute = (
    attribute: 'fill' | 'stroke' | 'strokeWidth' | 'opacity' | 'textContent' | 'all'
  ) => {
    if (!node || !selectedSubElementId) {
      console.warn('handleResetAttribute: 缺少 node 或 selectedSubElementId');
      return;
    }
    console.log('handleResetAttribute:', { nodeId: node.id, subElementId: selectedSubElementId, attribute });
    resetSubElementAttribute(node.id, selectedSubElementId, attribute);
  };

  const handleEnterSubEditMode = () => {
    if (!node) return;
    parseNodeSubElements(node.id);
    enterSubElementEditMode(node.id);
    setActiveTab('subElements');
  };

  const handleExportSvg = () => {
     const svgEl = (window as any).__AGI_SVG_REF__ as SVGSVGElement;
     if (svgEl) {
       exportSvgFile(svgEl.innerHTML);
     } else {
       alert('导出失败：无法获取画布内容');
     }
  };

  const renderGlobalActions = () => (
    <div className="ai-svg-actions border-b border-slate-200 mb-2 pb-2 px-4 pt-2 flex gap-2">
      <button 
        className="ai-svg-secondary-button-light flex-1 gap-1.5" 
        onClick={() => saveCanvas(true)}
        title="保存为 JSON 文件"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v13a2 2 0 0 1-2 2z"/>
          <polyline points="17 21 17 13 7 13 7 21"/>
          <polyline points="7 3 7 8 15 8"/>
        </svg>
        保存 JSON
      </button>
      <button 
        className="ai-svg-secondary-button-light flex-1 gap-1.5" 
        onClick={handleExportSvg}
        title="导出为 SVG"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
          <polyline points="7 10 12 15 17 10"/>
          <line x1="12" y1="15" x2="12" y2="3"/>
        </svg>
        导出 SVG
      </button>
    </div>
  );

  const renderTabs = () => (
    <div className="flex border-b border-slate-200 px-2">
      {(['basic', 'animation', 'subElements'] as TabType[]).map(tab => (
        <button
          key={tab}
          className={`px-3 py-2 text-xs font-medium transition-colors ${
            activeTab === tab 
              ? 'text-blue-600 border-b-2 border-blue-600' 
              : 'text-slate-500 hover:text-slate-700'
          }`}
          onClick={() => setActiveTab(tab)}
        >
          {tab === 'basic' && '基本'}
          {tab === 'animation' && '动画'}
          {tab === 'subElements' && '子元素'}
        </button>
      ))}
    </div>
  );

  const renderBasicProperties = () => (
    <div className="ai-svg-properties-form">
      <div className="ai-svg-form-row">
        <label className="ai-svg-form-label">名称</label>
        <input
          className="ai-svg-form-input"
          value={node?.name ?? ''}
          onChange={(e) => handleChange('name', e.target.value)}
          disabled={isDisabled}
          placeholder={isDisabled ? "未选中元素" : "输入名称"}
        />
      </div>
      <div className="ai-svg-form-row">
        <label className="ai-svg-form-label">X</label>
        <input
          className="ai-svg-form-input"
          type="number"
          value={node?.x ?? ''}
          onChange={(e) => handleChange('x', e.target.value)}
          disabled={isDisabled}
        />
      </div>
      <div className="ai-svg-form-row">
        <label className="ai-svg-form-label">Y</label>
        <input
          className="ai-svg-form-input"
          type="number"
          value={node?.y ?? ''}
          onChange={(e) => handleChange('y', e.target.value)}
          disabled={isDisabled}
        />
      </div>
      <div className="ai-svg-form-row">
        <label className="ai-svg-form-label">缩放</label>
        <input
          className="ai-svg-form-input"
          type="number"
          step={0.1}
          value={node?.scale ?? ''}
          onChange={(e) => handleChange('scale', e.target.value)}
          disabled={isDisabled}
        />
      </div>
      <div className="ai-svg-form-row">
        <label className="ai-svg-form-label">旋转</label>
        <input
          className="ai-svg-form-input"
          type="number"
          value={node?.rotation ?? ''}
          onChange={(e) => handleChange('rotation', e.target.value)}
          disabled={isDisabled}
        />
      </div>
      <div className="ai-svg-form-row">
        <label className="ai-svg-form-label">透明度</label>
        <input
          className="ai-svg-form-input"
          type="number"
          min={0}
          max={1}
          step={0.1}
          value={node?.opacity ?? ''}
          onChange={(e) => handleChange('opacity', e.target.value)}
          disabled={isDisabled}
        />
      </div>
    </div>
  );

  const renderAnimationProperties = () => {
    const parseTransformOrigin = (): { x: string; y: string } => {
      const origin = node?.animation?.transformOrigin || '';
      const parts = origin.trim().split(/\s+/);
      return {
        x: parts[0] || '',
        y: parts[1] || '',
      };
    };

    const handleOriginChange = (axis: 'x' | 'y', value: string) => {
      if (!node) return;
      const current = parseTransformOrigin();
      const newOrigin = axis === 'x' 
        ? `${value} ${current.y}`.trim()
        : `${current.x} ${value}`.trim();
      handleAnimationChange('transformOrigin', newOrigin);
    };

    const origin = parseTransformOrigin();
    const showOriginFields = node?.animation?.animationType === 'rotate' || node?.animation?.animationType === 'scale';

    return (
      <div className="ai-svg-properties-form">
        <div className="ai-svg-form-row">
          <label className="ai-svg-form-label">动画类型</label>
          <select
            className="ai-svg-form-input"
            value={node?.animation?.animationType ?? 'none'}
            onChange={(e) => handleAnimationChange('animationType', e.target.value)}
            disabled={isDisabled}
          >
            <option value="none">无</option>
            <option value="rotate">旋转</option>
            <option value="scale">缩放</option>
            <option value="translate">位移</option>
            <option value="opacity">淡入淡出</option>
          </select>
        </div>
        
        <div className="ai-svg-form-row">
          <label className="ai-svg-form-label">持续时间</label>
          <input
            className="ai-svg-form-input"
            value={node?.animation?.duration ?? ''}
            onChange={(e) => handleAnimationChange('duration', e.target.value)}
            disabled={isDisabled}
            placeholder="例如: 2s"
          />
        </div>
        
        <div className="ai-svg-form-row">
          <label className="ai-svg-form-label">重复次数</label>
          <input
            className="ai-svg-form-input"
            value={node?.animation?.repeatCount ?? ''}
            onChange={(e) => handleAnimationChange('repeatCount', e.target.value)}
            disabled={isDisabled}
            placeholder="indefinite 或 数字"
          />
        </div>
        
        <div className="ai-svg-form-row">
          <label className="ai-svg-form-label">缓动</label>
          <select
            className="ai-svg-form-input"
            value={node?.animation?.easing ?? 'ease'}
            onChange={(e) => handleAnimationChange('easing', e.target.value)}
            disabled={isDisabled}
          >
            <option value="linear">线性</option>
            <option value="ease">缓动</option>
            <option value="ease-in">缓入</option>
            <option value="ease-out">缓出</option>
            <option value="ease-in-out">缓入缓出</option>
          </select>
        </div>

        {showOriginFields && (
          <>
            <div className="ai-svg-form-row">
              <label className="ai-svg-form-label">中心点 X</label>
              <input
                className="ai-svg-form-input"
                type="number"
                value={origin.x}
                onChange={(e) => handleOriginChange('x', e.target.value)}
                disabled={isDisabled}
                placeholder="自动"
              />
            </div>
            <div className="ai-svg-form-row">
              <label className="ai-svg-form-label">中心点 Y</label>
              <input
                className="ai-svg-form-input"
                type="number"
                value={origin.y}
                onChange={(e) => handleOriginChange('y', e.target.value)}
                disabled={isDisabled}
                placeholder="自动"
              />
            </div>
            {node?.animation?.isOriginalAnimation && (
              <p className="text-xs text-green-600 px-2 py-1">
                ✓ 已从原始 SVG 提取中心点
              </p>
            )}
          </>
        )}
      </div>
    );
  };

  const renderSubElementTree = (elements: SvgSubElement[], depth = 0) => (
    <ul className="pl-2">
      {elements.map(el => (
        <li key={el.id}>
          <button
            className={`w-full text-left px-2 py-1 text-xs rounded ${
              selectedSubElementId === el.id 
                ? 'bg-blue-100 text-blue-700' 
                : 'hover:bg-slate-100'
            }`}
            style={{ paddingLeft: `${depth * 12 + 8}px` }}
            onClick={() => selectSubElement(el.id)}
          >
            <span className="font-mono text-slate-400 mr-1">&lt;{el.tagName}&gt;</span>
            {el.originalAttributes.id && (
              <span className="text-slate-500">#{el.originalAttributes.id}</span>
            )}
            {el.tagName === 'text' && el.originalTextContent && (
              <span className="text-slate-400 ml-1 truncate max-w-[80px] inline-block align-bottom">
                "{el.originalTextContent.substring(0, 10)}{el.originalTextContent.length > 10 ? '...' : ''}"
              </span>
            )}
          </button>
          {el.children && renderSubElementTree(el.children, depth + 1)}
        </li>
      ))}
    </ul>
  );

  /**
   * 渲染颜色编辑器
   * 支持纯色和渐变
   */
  const renderColorEditor = (
    label: string,
    currentValue: string | undefined,
    originalValue: string | undefined,
    isGradient: boolean | undefined,
    field: 'fill' | 'stroke',
    hasCustomValue: boolean
  ) => {
    const normalizedColor = normalizeColor(currentValue);
    const gradientId = getGradientId(originalValue);
    const isCurrentGradient = normalizedColor === null;
    
    return (
      <div className="ai-svg-form-row">
        <label className="ai-svg-form-label">{label}</label>
        <div className="flex items-center gap-2 flex-1">
          {isCurrentGradient || (isGradient && !hasCustomValue) ? (
            // 渐变显示
            <div className="flex items-center gap-2 flex-1">
              <div 
                className="w-8 h-8 rounded border border-slate-300"
                style={{
                  background: 'linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%), linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%)',
                  backgroundSize: '8px 8px',
                  backgroundPosition: '0 0, 4px 4px',
                }}
                title={`渐变: ${gradientId || '未知'}`}
              >
                <div 
                  className="w-full h-full rounded"
                  style={{ background: 'linear-gradient(135deg, #FFA500, #00FFFF)' }}
                />
              </div>
              <span className="text-xs text-slate-500">
                渐变 {gradientId ? `#${gradientId}` : ''}
              </span>
              <button
                className="text-xs text-blue-600 hover:text-blue-800 ml-auto"
                onClick={() => handleSubElementChange(field, '#000000')}
                title="替换为纯色"
              >
                改为纯色
              </button>
            </div>
          ) : (
            // 纯色编辑器
            <>
              <input
                className="ai-svg-form-input w-12 h-8 p-0 border-0"
                type="color"
                value={normalizedColor || '#000000'}
                onChange={(e) => handleSubElementChange(field, e.target.value)}
              />
              <span className="text-xs text-slate-500 font-mono flex-1">
                {normalizedColor || currentValue || '未设置'}
              </span>
              {hasCustomValue && (
                <button
                  className="text-xs text-orange-600 hover:text-orange-800"
                  onClick={() => handleResetAttribute(field)}
                  title={isGradient ? '恢复为渐变' : '恢复原始颜色'}
                >
                  恢复
                </button>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  const renderSubElementProperties = () => {
    // 获取当前显示的值（优先使用自定义值，否则使用原始值）
    const getCurrentFill = (): string | undefined => {
      if (!activeSubElement) return undefined;
      return activeSubElement.customAttributes.fill ?? activeSubElement.originalFill;
    };
    
    const getCurrentStroke = (): string | undefined => {
      if (!activeSubElement) return undefined;
      return activeSubElement.customAttributes.stroke ?? activeSubElement.originalStroke;
    };
    
    const getCurrentTextContent = (): string => {
      if (!activeSubElement) return '';
      return activeSubElement.customAttributes.textContent ?? activeSubElement.originalTextContent ?? '';
    };
    
    const hasCustomFill = activeSubElement?.customAttributes.fill !== undefined;
    const hasCustomStroke = activeSubElement?.customAttributes.stroke !== undefined;
    const hasCustomText = activeSubElement?.customAttributes.textContent !== undefined;

    return (
      <div className="ai-svg-properties-form">
        {!isSubElementEditMode ? (
          <div className="p-4">
            <button
              className="ai-svg-secondary-button-light w-full"
              onClick={handleEnterSubEditMode}
              disabled={isDisabled}
            >
              进入子元素编辑模式
            </button>
            <p className="text-xs text-slate-500 mt-2">
              双击画布上的 SVG 也可进入编辑模式
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200">
              <span className="text-xs font-medium">子元素列表</span>
              <button
                className="text-xs text-blue-600 hover:text-blue-800"
                onClick={exitSubElementEditMode}
              >
                退出编辑
              </button>
            </div>
            
            {/* 子元素树 */}
            <div className="max-h-40 overflow-y-auto border-b border-slate-200">
              {node?.subElements && renderSubElementTree(node.subElements)}
            </div>
            
            {/* 选中子元素的属性 */}
            {activeSubElement && (
              <div className="p-2">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-slate-600">
                    编辑: &lt;{activeSubElement.tagName}&gt;
                  </span>
                  <button
                    className="text-xs text-red-600 hover:text-red-800"
                    onClick={() => handleResetAttribute('all')}
                    title="恢复所有原始属性"
                  >
                    全部恢复
                  </button>
                </div>
                
                {/* 填充色 */}
                {renderColorEditor(
                  '填充色',
                  getCurrentFill(),
                  activeSubElement.originalFill,
                  activeSubElement.isFillGradient,
                  'fill',
                  hasCustomFill
                )}
                
                {/* 描边色 */}
                {renderColorEditor(
                  '描边色',
                  getCurrentStroke(),
                  activeSubElement.originalStroke,
                  activeSubElement.isStrokeGradient,
                  'stroke',
                  hasCustomStroke
                )}
                
                {/* 描边宽度 */}
                <div className="ai-svg-form-row">
                  <label className="ai-svg-form-label">描边宽度</label>
                  <div className="flex items-center gap-2 flex-1">
                    <input
                      className="ai-svg-form-input flex-1"
                      type="number"
                      min={0}
                      step={0.5}
                      value={activeSubElement.customAttributes.strokeWidth ?? activeSubElement.originalStrokeWidth ?? 1}
                      onChange={(e) => handleSubElementChange('strokeWidth', parseFloat(e.target.value))}
                    />
                    {activeSubElement.customAttributes.strokeWidth !== undefined && (
                      <button
                        className="text-xs text-orange-600 hover:text-orange-800"
                        onClick={() => handleResetAttribute('strokeWidth')}
                      >
                        恢复
                      </button>
                    )}
                  </div>
                </div>
                
                {/* 透明度 */}
                <div className="ai-svg-form-row">
                  <label className="ai-svg-form-label">透明度</label>
                  <div className="flex items-center gap-2 flex-1">
                    <input
                      className="ai-svg-form-input flex-1"
                      type="number"
                      min={0}
                      max={1}
                      step={0.1}
                      value={activeSubElement.customAttributes.opacity ?? activeSubElement.originalOpacity ?? 1}
                      onChange={(e) => handleSubElementChange('opacity', parseFloat(e.target.value))}
                    />
                    {activeSubElement.customAttributes.opacity !== undefined && (
                      <button
                        className="text-xs text-orange-600 hover:text-orange-800"
                        onClick={() => handleResetAttribute('opacity')}
                      >
                        恢复
                      </button>
                    )}
                  </div>
                </div>
                
                {/* X 偏移 */}
                <div className="ai-svg-form-row">
                  <label className="ai-svg-form-label">X 偏移</label>
                  <input
                    className="ai-svg-form-input"
                    type="number"
                    value={activeSubElement.customAttributes.offsetX ?? 0}
                    onChange={(e) => handleSubElementChange('offsetX', parseFloat(e.target.value))}
                  />
                </div>
                
                {/* Y 偏移 */}
                <div className="ai-svg-form-row">
                  <label className="ai-svg-form-label">Y 偏移</label>
                  <input
                    className="ai-svg-form-input"
                    type="number"
                    value={activeSubElement.customAttributes.offsetY ?? 0}
                    onChange={(e) => handleSubElementChange('offsetY', parseFloat(e.target.value))}
                  />
                </div>
                
                {/* 文本内容（仅 text 元素） */}
                {activeSubElement.tagName === 'text' && (
                  <div className="ai-svg-form-row">
                    <label className="ai-svg-form-label">文本内容</label>
                    <div className="flex items-center gap-2 flex-1">
                      <input
                        className="ai-svg-form-input flex-1"
                        type="text"
                        value={getCurrentTextContent()}
                        onChange={(e) => handleSubElementChange('textContent', e.target.value)}
                        placeholder="输入文本..."
                      />
                      {hasCustomText && (
                        <button
                          className="text-xs text-orange-600 hover:text-orange-800"
                          onClick={() => handleResetAttribute('textContent')}
                        >
                          恢复
                        </button>
                      )}
                    </div>
                  </div>
                )}
                
                {/* 原始值信息 */}
                <div className="mt-3 p-2 bg-slate-50 rounded text-xs text-slate-500">
                  <div className="font-medium mb-1">原始值:</div>
                  {activeSubElement.originalFill && (
                    <div>填充: {activeSubElement.isFillGradient ? `渐变 ${getGradientId(activeSubElement.originalFill)}` : activeSubElement.originalFill}</div>
                  )}
                  {activeSubElement.originalStroke && (
                    <div>描边: {activeSubElement.isStrokeGradient ? `渐变 ${getGradientId(activeSubElement.originalStroke)}` : activeSubElement.originalStroke}</div>
                  )}
                  {activeSubElement.originalTextContent && (
                    <div>文本: "{activeSubElement.originalTextContent}"</div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const renderAlignmentTools = () => (
    <div className="flex flex-col px-4 py-2 border-b border-slate-200 mb-2">
      <label className="ai-svg-form-label mb-2">对齐工具 ({selectedState.selectedIds.length} 选中)</label>
      <div className="flex flex-wrap gap-2">
         <button className="ai-svg-secondary-button-light w-8" title="左对齐" onClick={() => alignSelectedElements('left')}>L</button>
         <button className="ai-svg-secondary-button-light w-8" title="居中对齐" onClick={() => alignSelectedElements('center')}>C</button>
         <button className="ai-svg-secondary-button-light w-8" title="右对齐" onClick={() => alignSelectedElements('right')}>R</button>
         <button className="ai-svg-secondary-button-light w-8" title="顶对齐" onClick={() => alignSelectedElements('top')}>T</button>
         <button className="ai-svg-secondary-button-light w-8" title="垂直居中" onClick={() => alignSelectedElements('middle')}>M</button>
         <button className="ai-svg-secondary-button-light w-8" title="底对齐" onClick={() => alignSelectedElements('bottom')}>B</button>
      </div>
    </div>
  );
  
  // 多选状态
  if (multiSelection) {
     return (
       <div className="ai-svg-panel ai-svg-panel-right">
        <div className="ai-svg-panel-header">
          <span className="ai-svg-panel-title">多选属性</span>
        </div>
        {renderGlobalActions()}
        {renderAlignmentTools()}
        <div className="ai-svg-empty-state">已选中 {selectedState.selectedIds.length} 个元素</div>
       </div>
     );
  }

  return (
    <div className="ai-svg-panel ai-svg-panel-right">
      <div className="ai-svg-panel-header">
        <span className="ai-svg-panel-title">属性面板</span>
      </div>
      {renderGlobalActions()}
      {renderTabs()}
      
      {activeTab === 'basic' && renderBasicProperties()}
      {activeTab === 'animation' && renderAnimationProperties()}
      {activeTab === 'subElements' && renderSubElementProperties()}
    </div>
  );
};

export default PropertiesPanel;